/**
 * Date formatting utilities for PSM Shipping Portal.
 * All timestamps are displayed in US Eastern Time (ET).
 */

const EASTERN_TZ = 'America/New_York';

/**
 * Parse a date string from the API.
 * - Naive MySQL DATETIME (no timezone suffix) → treated as Eastern wall-clock time
 * - ISO string with Z or offset → absolute instant
 * - Date-only YYYY-MM-DD → calendar date, no shift
 */
function toDate(dateString, { dateOnly = false } = {}) {
  if (!dateString) return null;
  if (dateString instanceof Date) return dateString;
  const s = String(dateString).trim();

  if (dateOnly || /^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, m, d] = s.slice(0, 10).split('-').map(Number);
    return new Date(y, m - 1, d);
  }

  // Explicit UTC or offset — use as-is
  if (s.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(s)) {
    return new Date(s);
  }

  // Naive datetime from MySQL — interpret as Eastern wall-clock
  const normalized = s.replace(' ', 'T');
  const match = normalized.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?/);
  if (!match) return new Date(s);

  const [, year, month, day, hour, minute, second = '0'] = match;
  let utcMs = Date.UTC(+year, +month - 1, +day, +hour, +minute, +second);

  // Iteratively correct for Eastern offset
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: EASTERN_TZ,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false,
  });

  for (let i = 0; i < 3; i++) {
    const parts = Object.fromEntries(
      dtf.formatToParts(new Date(utcMs))
        .filter(p => p.type !== 'literal')
        .map(p => [p.type, p.value])
    );
    let h = Number(parts.hour);
    if (h === 24) h = 0;
    const shown = Date.UTC(+parts.year, +parts.month - 1, +parts.day, h, +parts.minute, +parts.second);
    const wanted = Date.UTC(+year, +month - 1, +day, +hour, +minute, +second);
    utcMs += wanted - shown;
  }

  return new Date(utcMs);
}

// Timezone was corrected from Pacific to Eastern on August 8, 2026 (PROD deployment date)
const TIMEZONE_FIX_DATE = new Date('2026-08-08T00:00:00Z');

/**
 * Format a datetime string as Eastern time.
 * Only appends "ET" label for shipments created after the timezone fix.
 * Output: "08/05/2026, 05:00 PM ET" (new) or "08/03/2026, 02:23 PM" (old)
 */
export function formatDateTimeEST(dateString) {
  const date = toDate(dateString);
  if (!date || isNaN(date.getTime())) return '-';
  const formatted = date.toLocaleString('en-US', {
    timeZone: EASTERN_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
  // Only show ET label for shipments after the timezone was corrected
  const showETLabel = date >= TIMEZONE_FIX_DATE;
  return showETLabel ? `${formatted} ET` : formatted;
}

/**
 * Format a date-only string (pickup/delivery dates).
 * Output: "08/10/2026"
 */
export function formatDateEST(dateString) {
  const date = toDate(dateString, { dateOnly: true });
  if (!date || isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}
